import type * as maplibregl from 'maplibre-gl'
import { useEffect, useRef, useState } from 'react'

/**
 * Returns debounced map bounds that update on MapLibre `moveend` events.
 * Returns null until the map fires its first moveend or when map is null.
 */
export const useMapBounds = (
  map: maplibregl.Map | null,
  debounceMs = 500,
): maplibregl.LngLatBounds | null => {
  const [bounds, setBounds] = useState<maplibregl.LngLatBounds | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!map) return

    setBounds(map.getBounds())

    const handler = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        setBounds(map.getBounds())
      }, debounceMs)
    }

    map.on('moveend', handler)

    return () => {
      map.off('moveend', handler)
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [map, debounceMs])

  return bounds
}
